import { marketDate } from './earningsDates.js'
import { isUsEquityTradingDay, isUsEquityEarlyCloseDay } from '../marketSession.js'
import { toChartTimestamp } from '../timezone.js'
import { computeWindowVWAP } from './vwapWindow.js'

const unavailable = message => ({ status: 'unavailable', message, data: [] })
const validDate = date => typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
  && Number.isFinite(Date.parse(date)) && new Date(date).toISOString().slice(0, 10) === date
const noon = date => new Date(`${date}T12:00:00Z`)
const instant = (date, time) => toChartTimestamp(`${date.replaceAll('-', '')} ${time} US/Eastern`, 'UTC') * 1000

export function resolveEarningsAnchor(events, now = new Date(), tradingCalendar, anchorTiming = 'earnings-date', reportCount = 1) {
  if (![1, 2].includes(reportCount)) return unavailable('Unsupported earnings window')
  if (!Number.isFinite(now.getTime())) return unavailable('Earnings time unavailable')
  const today = marketDate(now)
  const candidates = (Array.isArray(events) ? events : []).filter(event => {
    if (!validDate(event?.date) || event.date > today) return false
    // A same-day after-close record must not replace the last report before close.
    if (event.date === today && event.session === 'postmarket') {
      const close = isUsEquityEarlyCloseDay(noon(today), tradingCalendar) ? '13:00:00' : '16:00:00'
      return now.getTime() >= instant(today, close)
    }
    return true
  }).sort((a, b) => b.date.localeCompare(a.date))
  const event = candidates[reportCount - 1]
  if (!event) return unavailable(reportCount === 2 ? 'Two reported earnings are not available' : 'No reported earnings available')
  // EPS coverage is not earnings-date coverage (PURR supplies a company date
  // with no actual EPS). Accept elapsed company-sourced dates, but require
  // actual report evidence for today so a scheduled release cannot move early.
  if (candidates.slice(0, reportCount).some(item => item.source !== 'company' || (!item.reported && item.date === today))) {
    return unavailable('Latest earnings report is not confirmed')
  }
  const afterRelease = anchorTiming === 'after-release'
  if (afterRelease && !['premarket', 'postmarket'].includes(event.session)) return unavailable('Earnings release timing unavailable')
  let date = event.date
  for (let offset = afterRelease && event.session === 'postmarket' ? 1 : 0; offset <= 14; offset++) {
    date = new Date(Date.parse(event.date) + offset * 86400000).toISOString().slice(0, 10)
    if (!isUsEquityTradingDay(noon(date), tradingCalendar)) continue
    const anchorEpochMs = instant(date, '09:30:00')
    const waiting = now.getTime() < anchorEpochMs
    return { status: waiting ? 'waiting-open' : 'ready', event, anchorDate: date, anchorEpochMs,
      message: `${waiting ? 'Waiting for' : 'Anchored'} ${date} · 09:30 ET` }
  }
  return unavailable('Earnings trading session unavailable')
}

export function computeEarningsVwap(bars, anchor, timeframe) {
  if (anchor?.status !== 'ready') return { ...anchor, data: [] }
  if (timeframe === 'weekly') return unavailable('Use a daily or intraday chart for the earnings session anchor')
  if (!Array.isArray(bars) || !bars.length) return unavailable('Waiting for price history')
  if (bars.some((bar, index) => !Number.isFinite(bar.time) || (index > 0 && bar.time <= bars[index - 1].time))) {
    return unavailable('Earnings VWAP history is out of order')
  }
  const daily = timeframe === 'daily'
  const key = bar => daily
    ? (Number.isFinite(bar?.time) ? new Date(bar.time * 1000).toISOString().slice(0, 10) : null)
    : (Number.isFinite(bar?.rawEpochMs) ? bar.rawEpochMs : null)
  const start = daily ? anchor.anchorDate : anchor.anchorEpochMs
  // Never treat the first available bar as the requested anchor. This matters
  // when an intraday chart has not loaded the whole earnings window yet.
  const startIndex = bars.findIndex(bar => key(bar) === start)
  if (startIndex < 0) return unavailable(`Load price history back to ${anchor.anchorDate} · 09:30 ET`)
  const window = bars.slice(startIndex)
  let previousTime = -Infinity
  let previousKey = null
  for (const bar of window) {
    const barKey = key(bar)
    if (barKey == null || barKey < start || (previousKey != null && barKey <= previousKey)
      || !Number.isFinite(bar.time) || bar.time <= previousTime
      || !['open', 'high', 'low', 'close'].every(field => Number.isFinite(bar[field]))
      || bar.volume == null || bar.volume === '' || !Number.isFinite(Number(bar.volume)) || Number(bar.volume) < 0) {
      return unavailable('Earnings VWAP price or volume history is incomplete')
    }
    previousTime = bar.time
    previousKey = barKey
  }
  const data = computeWindowVWAP(window)
  return data.length ? { ...anchor, data } : unavailable('Waiting for earnings-session volume')
}
